<template>
    <div v-if="selectIsAuthenticated" class="user-bar">
        <div class="user-bar-wrap">
            <div class="user-bar-copy">
                <div class="notification-bar">
                    <v-btn
                        v-if="!size(selectMessage)"
                        rounded
                        class="text-none mr-4"
                        stacked
                        icon
                        size="small"
                        readonly
                        :ripple="false"
                        style="max-height: 40px; max-width: 40px"
                    >
                        <v-icon>circle_notifications</v-icon>
                    </v-btn>

                    <v-menu v-else v-model="notificationMenu" :close-on-content-click="false">
                        <template #activator="{ props }">
                            <v-btn
                                class="text-none mr-4"
                                stacked
                                v-bind="props"
                                icon
                                size="small"
                                :ripple="false"
                                rounded
                                style="max-height: 40px; max-width: 40px"
                            >
                                <v-badge color="info" :content="size(selectMessage)">
                                    <v-icon icon="circle_notifications"></v-icon>
                                </v-badge>
                            </v-btn>
                        </template>
                        <v-list class="notification-list">
                            <v-list-subheader>{{ t('label_notifications') }}</v-list-subheader>
                            <v-list-item
                                v-for="(message, msgI) in selectMessage"
                                :key="message.task_id"
                                :class="msgI % 2 ? 'notification-item' : 'notification-item odd'"
                            >
                                <template #prepend>
                                    <v-avatar :color="message.status === 'success' ? 'success' : 'error'">
                                        <v-icon
                                            :icon="message.status === 'success' ? 'task' : 'cancel_presentation'"
                                            color="white"
                                        ></v-icon>
                                    </v-avatar>
                                </template>

                                <template #title>
                                    <v-label class="text-h6">
                                        {{
                                            isArray(message.message) && size(message.message)
                                                ? t('implement_imported_successfuly', {
                                                    file: get(
                                                        find(selectTasks, (x) => x.task_id === message.task_id),
                                                        'file_name'
                                                    ),
                                                })
                                                : t('implement_imported_fail', {
                                                    file: get(
                                                        find(selectTasks, (x) => x.task_id === message.task_id),
                                                        'file_name'
                                                    ),
                                                })
                                        }}
                                    </v-label>
                                </template>
                                <template v-if="isArray(message.message) && size(message.message)" #subtitle>
                                    {{
                                        t('implement_imported_successfuly_message', {
                                            size: size(message.message),
                                        })
                                    }}
                                </template>

                                <template #default>
                                    <ul v-if="isArray(message.message) && size(message.message)">
                                        <div v-for="(item, index) in message.message" :key="item" :index="index">
                                            <v-divider v-if="index !== 0"></v-divider>
                                            <button @click="navigateToImplement(item as string)">
                                                <li style="cursor: pointer" class="py-2">
                                                    {{ implementsMap[item]?.implement.name }}
                                                    <small
                                                    >({{ size(implementsMap[item]?.implement.specs) }})
                                                        {{ t('label_properties') }}</small
                                                    >
                                                </li>
                                            </button>
                                        </div>
                                    </ul>
                                    <v-list-item-action class="flex-column align-end">
                                        <small class="mb-4 text-high-emphasis opacity-60"
                                        >{{ new Date(message.completed_at).toLocaleDateString() }}
                                            {{ new Date(message.completed_at).toLocaleTimeString() }}</small
                                        >
                                    </v-list-item-action>
                                </template>

                                <template #append>
                                    <v-tooltip :text="t('tooltip_mark_as_read')" location="start">
                                        <template #activator="{ props }">
                                            <v-btn
                                                v-bind="props"
                                                color="grey-lighten-1"
                                                icon="do_not_disturb_on"
                                                variant="text"
                                                @click="completeMessage(message.task_id)"
                                            ></v-btn>
                                        </template>
                                    </v-tooltip>
                                </template>
                            </v-list-item>
                        </v-list>
                    </v-menu>
                </div>
                <v-label for="search-bar" class="user-bar-copy-label"
                >{{ t('navigation_text_user_hello') }},
                    {{
                        selectAccessTokenUserName === '' ? t('login_default_username') : selectAccessTokenUserName
                    }}</v-label
                >
                <v-btn v-if="isDevelopment()" icon="account_circle"></v-btn>
                <v-menu v-else>
                    <template #activator="{ props }">
                        <v-btn icon="account_circle" v-bind="props"></v-btn>
                    </template>
                    <v-list>
                        <v-list-item
                            v-for="(item, index) in navigation"
                            :key="index"
                            :value="index"
                            color="primary"
                            @click="navigateTo(item.link)"
                        >
                            <v-list-item-title>
                                {{ t(item.text) }}
                            </v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { onBeforeUnmount, onMounted, onUnmounted, reactive, ref, watch } from 'vue';
import { APP_USER_MENU } from '../../../config/app.config';
import type { INavigationItem } from '../../../shared/interfaces/navigation-item.interface';
import useAuthStore from '../../auth/store/auth.store';
import { storeToRefs } from 'pinia';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { msalInstance } from '../../../config/msalConfig';
import { AuthService } from '../../auth/services/auth.service';
import { LocalStorageService } from '@/shared/services/local-storage.service';
import useUIStore from '../store/ui.store';
import { filter, find, flatMap, forEach, get, isArray, parseInt, size, split, toString, uniq } from 'lodash';
import apiClient from '@/shared/services/api-client.service';
import type { IImplement } from '@/shared/interfaces/implement.interface';

const { t } = useI18n();
const router = useRouter();
const authStore = useAuthStore();
const uiStore = useUIStore();
const authServices = new AuthService();
const { selectAccessTokenUserName, selectIsAuthenticated } = storeToRefs(authStore);
const navigation: INavigationItem[] = reactive(APP_USER_MENU);

const localStorage = new LocalStorageService();

const { selectTasks, selectMessage } = storeToRefs(uiStore);

const notificationMenu = ref<boolean>(false);

const implementsMap = ref<Record<string, IImplement | undefined>>({});

const connection = ref<WebSocket | null>(null);

const isConnected = ref(false);
const pingTimer = ref<number | null>(null);

const isDevelopment = (): boolean => {
    if (import.meta.env.VITE_APP_MODE === 'development' && import.meta.env.VITE_APP_DEPLOYMENT_MODE === 'local') {
        return true;
    } else if (import.meta.env.VITE_APP_DEPLOYMENT_MODE !== 'local') {
        return false;
    }
    return false;
};

const navigateTo = async (name: string): Promise<void> => {
    if (name === 'MSALLogout' && isDevelopment() === false) {
        if (msalInstance !== null) {
            return authServices.logoutMSAL();
        }
    }
    await router.push({ name });
};

// const sendPing = throttle(
//     () => {
//         if (connection.value && connection.value.readyState === WebSocket.OPEN) {
//             connection.value.send(JSON.stringify({ type: 'ping' }));
//         }
//     },
//     parseInt(toString(import.meta.env.VITE_WS_PING_TIME)) || 5000
// );

onMounted(() => {
    uiStore.setTasksId(uniq(filter(split(localStorage.getItem('tasks_id') as string, ','))));
});

onUnmounted(() => {});

onBeforeUnmount(() => {
    if (connection.value) {
        connection.value.close();
    }
    if (pingTimer.value) {
        clearInterval(pingTimer.value);
    }
});

watch(selectMessage, (data) => {
    const filtered = filter(data, { status: 'success' });
    const messages = flatMap(filtered, 'message');
    forEach(messages, (item) => {
        getImplementData(item);
    });
});

function completeMessage(task_id: string): void {
    uiStore.completeMessages(task_id);
}

async function navigateToImplement(id: string): Promise<void> {
    await router
        .push({
            name: 'ImplementsHome',
        })
        .finally(async () => {
            await router.push({
                name: 'ViewImplement',
                params: {
                    id,
                },
            });
            notificationMenu.value = false;
        });
}

function getImplementData(id: string): void {
    apiClient
        .get(`/implements/${id}`)
        .then((response) => {
            implementsMap.value[id] = response.data;
        })
        .catch(() => {
            implementsMap.value[id] = undefined;
        });
}

function init(tasks: { task_id: string; file_name: string }[]): void {
    connection.value =
        import.meta.env.VITE_APP_DEPLOYMENT_MODE === 'local'
            ? new WebSocket(`${import.meta.env.VITE_WS_URL}`)
            : new WebSocket(`${import.meta.env.VITE_WS_URL}?subscription-key=${import.meta.env.VITE_API_KEY}`);

    if (connection.value === null) {
        return;
    }

    connection.value.onopen = (): void => {
        isConnected.value = true;
        // TBD with Gabriel
        // pingTimer.value = window.setInterval(
        //     () => {
        //         sendPing();
        //     },
        //     parseInt(toString(import.meta.env.VITE_WS_PING_TIME)) || 5000
        // );
        forEach(tasks, (task: { task_id: string; file_name: string }) => {
            if (connection.value === null) {
                return;
            }

            connection.value.send(
                JSON.stringify({
                    action: 'subscribe',
                    task_id: task.task_id as string,
                })
            );
        });
    };

    connection.value.onclose = (): void => {
        isConnected.value = false;
        if (pingTimer.value) clearInterval(pingTimer.value);
        setTimeout(() => init([]), parseInt(toString(import.meta.env.VITE_WS_RECONNECT_TIME)) || 3000);
    };

    connection.value.onmessage = (message): void => {
        uiStore.setMessage(JSON.parse(message.data));
    };
}

watch(
    selectTasks,
    (tasks: { task_id: string; file_name: string }[]) => {
        init(tasks);
    },
    { immediate: true, flush: 'post' }
);
</script>
<style lang="scss">
@use '../../../scss/variables' as v;

.notification-list {
    .notification-item {
        padding-top: 12px !important;
        padding-bottom: 6px !important;

        &:not(:last-child) {
            border-bottom: 1px solid v.$border-color;
        }

        &.odd {
            background-color: v.$netrual-100;
        }
    }
}

.user-bar {
    &-wrap {
        padding: 0 20px 0 12px;
    }

    &-copy {
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;

        &-label {
            font-size: 18px !important;
            font-weight: 500;
            margin-right: 6px;
        }

        .v-btn {
            .v-btn__content {
                .mdi {
                    font-size: 24px;
                    width: 24px;
                    height: 24px;
                }
            }
        }
    }
}
</style>
